import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Accordion, AccordionSummary, AccordionDetails, Pagination, Paper } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import RequirementHistoryItem from './RequirementHistoryItem';
import { fetchHistory } from '../../../store/strategistSlice';
import { COLORS } from '../../../shared/colors';

const BreakdownHistoryTab = ({ selectedProjects }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [lastFetchParams, setLastFetchParams] = useState(null);
  const limit = 10;
  
  const { history, loading, error } = useSelector((state) => state.strategist);
  const { data: historyData, totalPages } = history;

  useEffect(() => {
    const params = {
      type: 'breakdown',
      page,
      limit,
    };
    
    if (selectedProjects && selectedProjects.length > 0) {
      params.projects = selectedProjects;
    }
    
    const paramsString = JSON.stringify(params);
    if (paramsString !== lastFetchParams) {
      setLastFetchParams(paramsString);
      dispatch(fetchHistory(params));
    }
  }, [dispatch, selectedProjects, page, limit, lastFetchParams]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  if (loading.fetchHistory) {
    return (
      <Box className="history-loading">
        <CircularProgress />
        <Typography variant="body2" className="history-loading-text">
          {t('agent.history.loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className="history-error">
        {error}
      </Alert>
    );
  }

  if (historyData.length === 0) {
    return (
      <Box className="history-empty">
        <Typography variant="h6" className="history-empty-title">
          {t('agent.history.empty.title')}
        </Typography>
        <Typography variant="body2" className="history-empty-description">
          {t('agent.history.empty.description')}
        </Typography>
      </Box>
    );
  }

  // Group items by project
  const groupedByProject = historyData.reduce((acc, item) => {
    const projectName = item.project_name || 'Unknown Project';
    if (!acc[projectName]) {
      acc[projectName] = [];
    }
    acc[projectName].push(item);
    return acc;
  }, {});

  return (
    <Box className="breakdown-history-tab">
      {Object.entries(groupedByProject).map(([projectName, items]) => (
        <Accordion 
          key={projectName}
          defaultExpanded
          sx={{ 
            mb: 2, 
            border: `1px solid ${COLORS.gray[200]}`,
            '&:before': { display: 'none' },
            borderRadius: '10px !important',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08) !important',
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              bgcolor: '#dbeafe !important',
              borderBottom: `1px solid ${COLORS.gray[200]}`,
              '&:hover': { bgcolor: '#bfdbfe !important' },
              '& .MuiAccordionSummary-content': {
                alignItems: 'center'
              }
            }}
          >
            <Typography variant="h6" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {projectName}
              <Box
                sx={{
                  background: COLORS.primary,
                  color: 'white',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1
                }}
              >
                {items.length}
              </Box>
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ 
            p: 2, 
            bgcolor: COLORS.gray[25] || '#fafafa'
          }}>
            <Box className="history-items-list">
              {items.map((item) => {
                // Check if this is a failed item
                const isFailed = item.status === 'FAILED' || (item.agent_output && (item.agent_output.error || item.agent_output.parseError));
                const errorMessage = item.agent_output?.error || item.agent_output?.parseError || t('agent.history.agentProcessingError');
                
                if (isFailed) {
                  return (
                    <Paper key={`${item.project_id}-${item.requirement}`} elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${COLORS.gray[200]}` }}>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight="600">
                          {item.requirement}
                        </Typography>
                        {item.created_at && (
                          <Typography variant="caption" color="text.secondary">
                            {item.created_at}
                          </Typography>
                        )}
                      </Box>
                      <Alert severity="error">
                        {errorMessage}
                      </Alert>
                    </Paper>
                  );
                }
                
                return (
                  <RequirementHistoryItem
                    key={`${item.project_id}-${item.requirement}`}
                    requirement={item.requirement}
                    breakdown={item.agent_output}
                    tickets={item.generated_tickets || {}}
                    createdAt={item.created_at}
                    totalTicketCount={item.total_ticket_count}
                  />
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
      
      <Box className="history-pagination-container">
        <Pagination
          count={totalPages || 1}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
          disabled={!totalPages || totalPages <= 1}
          slotProps={{
            previous: { title: t('pagination.previous') },
            next: { title: t('pagination.next') },
            first: { title: t('pagination.first') },
            last: { title: t('pagination.last') }
          }}
        />
      </Box>
    </Box>
  );
};

export default BreakdownHistoryTab;

